import { ReplaySubject } from 'rxjs';
import axios from 'axios';
import { userService } from './user.service';

export interface ArticleImage {
    id: string;
    filename: string;
    mimeType: string;
    createdAt: string;
}

export interface Article {
    id: string;
    title: string;
    author: string;
    headlineImage?: string;
    tags: string[];
    markdownContent: string;
    status?: string;
    publishTime?: string;
    createdAt?: string;
    updatedAt?: string;
}

class ArticlesService {
    articleSubject = new ReplaySubject<Article[]>(1);

    constructor() {
        this.fetchPublishedArticles();
    }

    async fetchPublishedArticles() {
        try {
            const resp = await axios.get('/api/articles/published');
            this.articleSubject.next(resp.data);
        } catch (e) {
            console.error('Failed to fetch articles', e);
            this.articleSubject.next([]);
        }
    }

    getArticles() {
        return this.articleSubject.asObservable();
    }

    async getArticle(id: string): Promise<Article | null> {
        try {
            const resp = await axios.get(`/api/articles/${id}`);
            return resp.data;
        } catch (e) {
            console.error('Failed to fetch article', e);
            return null;
        }
    }

    async getAllArticles(): Promise<Article[]> {
        const authHeaders = { 'Authorization': 'Bearer ' + userService.jwt };
        const resp = await axios.get('/api/articles/admin/all', { headers: authHeaders });
        return resp.data;
    }

    async createArticle(): Promise<string> {
        const authHeaders = { 'Authorization': 'Bearer ' + userService.jwt };
        const resp = await axios.post('/api/articles', {}, { headers: authHeaders });
        return resp.data.id;
    }

    async updateArticle(id: string, article: Partial<Article>): Promise<void> {
        const authHeaders = { 'Authorization': 'Bearer ' + userService.jwt };
        await axios.put(`/api/articles/${id}`, {
            title: article.title,
            author: article.author,
            imageUrl: article.headlineImage,
            tags: article.tags,
            markdownContent: article.markdownContent
        }, { headers: authHeaders });
    }

    async publishArticle(id: string): Promise<void> {
        const authHeaders = { 'Authorization': 'Bearer ' + userService.jwt };
        await axios.post(`/api/articles/${id}/publish`, {}, { headers: authHeaders });
        this.fetchPublishedArticles();
    }

    async deleteArticle(id: string): Promise<void> {
        const authHeaders = { 'Authorization': 'Bearer ' + userService.jwt };
        await axios.delete(`/api/articles/${id}`, { headers: authHeaders });
    }

    uploadImage(articleId: string, file: File, onProgress: (pct: number) => void): Promise<ArticleImage> {
        return new Promise((resolve, reject) => {
            const formData = new FormData();
            formData.append('image', file);
            const xhr = new XMLHttpRequest();
            xhr.upload.onprogress = (e) => {
                if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
            };
            xhr.onload = () => {
                if (xhr.status === 200) resolve(JSON.parse(xhr.responseText));
                else reject(new Error('Upload failed'));
            };
            xhr.onerror = () => reject(new Error('Upload failed'));
            xhr.open('POST', `/api/articles/${articleId}/images`);
            xhr.setRequestHeader('Authorization', 'Bearer ' + userService.jwt);
            xhr.send(formData);
        });
    }

    async getArticleImages(articleId: string): Promise<ArticleImage[]> {
        const authHeaders = { 'Authorization': 'Bearer ' + userService.jwt };
        const resp = await axios.get(`/api/articles/${articleId}/images`, { headers: authHeaders });
        return resp.data;
    }

    async deleteImage(imageId: string): Promise<void> {
        const authHeaders = { 'Authorization': 'Bearer ' + userService.jwt };
        await axios.delete(`/api/articles/images/${imageId}`, { headers: authHeaders });
    }
}

export const articlesService = new ArticlesService();
